import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Plus, Loader2, Trash2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { BOARD } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

export default function Boards() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboards", { params: { workspace_id: active.id } });
      setBoards(data);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/dashboards", {
        workspace_id: active.id,
        name: name.trim() || "Untitled dashboard",
      });
      setName("");
      navigate(`/boards/${data.id}`);
    } catch {
      toast.error("Could not create dashboard.");
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    await api.delete(`/dashboards/${id}`);
    load();
  };

  return (
    <div data-testid={BOARD.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Dashboards</h1>
            <div aria-hidden="true" className="sema-accent-underline mt-4" />
            <p className="mt-5 max-w-xl text-sm text-muted-foreground">
              On-demand boards built from your chat answers. Add any chart from Ask,
              then explain, share or export it.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={BOARD.createInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create()}
              placeholder="New dashboard name"
              className="w-56 text-sm"
            />
            <Button
              data-testid={BOARD.newBtn}
              onClick={create}
              disabled={creating || !active}
              className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
            >
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              New
            </Button>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : boards.length ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {boards.map((b) => (
                <Link
                  key={b.id}
                  to={`/boards/${b.id}`}
                  data-testid={BOARD.card}
                  className="group relative rounded-xl border border-border/60 bg-card/50 p-5 transition-colors hover:border-accent/50"
                >
                  <LayoutDashboard className="h-5 w-5 text-accent" />
                  <h3 className="mt-4 flex items-center gap-1 truncate text-sm font-semibold">
                    {b.name}
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {b.widget_count ?? 0} visualization(s) · {b.share_mode === "public" ? "Public" : "Private"}
                  </p>
                  <button
                    onClick={(e) => remove(b.id, e)}
                    aria-label="Delete dashboard"
                    className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </Link>
              ))}
            </div>
          ) : (
            <div data-testid={BOARD.empty} className="rounded-xl border border-dashed border-border/60 p-12 text-center">
              <LayoutDashboard className="mx-auto h-8 w-8 text-accent" />
              <p className="mt-4 text-sm text-foreground/90">No dashboards yet.</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Create one above, then add charts from{" "}
                <Link to="/chat" className="text-accent underline">Ask</Link>.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
